/**
 * Pure date-range logic for Home "סדר היום" (day · week · month). No React,
 * no requests: every date is a local `YYYY-MM-DD` key, the week starts on
 * Sunday (Israeli week, PRD §4.19), and every helper is unit-tested
 * (`src/utils/__tests__/agendaModel.test.ts`).
 */

export type AgendaView = 'day' | 'week' | 'month';
export const AGENDA_VIEWS: AgendaView[] = ['day', 'week', 'month'];

/** YYYY-MM-DD in local time. */
export function toKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** A key as a local Date at midday (never shifts a day across DST or time zones). */
export function fromKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d, 12);
}

export function isDayKey(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && toKey(fromKey(value)) === value;
}

export function addDays(key: string, n: number): string {
  const d = fromKey(key);
  return toKey(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, 12));
}

/** Same day of the month `n` months away, clamped to that month's length (31 Jan → 28/29 Feb). */
export function addMonths(key: string, n: number): string {
  const d = fromKey(key);
  const first = new Date(d.getFullYear(), d.getMonth() + n, 1, 12);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0, 12).getDate();
  return toKey(new Date(first.getFullYear(), first.getMonth(), Math.min(d.getDate(), last), 12));
}

export function startOfWeek(key: string): string {
  return addDays(key, -fromKey(key).getDay());
}

export function startOfMonth(key: string): string {
  return `${key.slice(0, 7)}-01`;
}

export function endOfMonth(key: string): string {
  const d = fromKey(key);
  return toKey(new Date(d.getFullYear(), d.getMonth() + 1, 0, 12));
}

export function sameMonth(a: string, b: string): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

/** Every key from `from` to `to`, inclusive. */
export function daysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let k = from; k <= to; k = addDays(k, 1)) out.push(k);
  return out;
}

export interface AgendaRange {
  /** First day, inclusive. */
  from: string;
  /** Last day, inclusive. */
  to: string;
}

/** The period a view shows around the selected day: that day, its Sun–Sat week, or its month. */
export function rangeFor(view: AgendaView, selected: string): AgendaRange {
  if (view === 'day') return { from: selected, to: selected };
  if (view === 'week') {
    const from = startOfWeek(selected);
    return { from, to: addDays(from, 6) };
  }
  return { from: startOfMonth(selected), to: endOfMonth(selected) };
}

/** ‹ / › : the selected day one period earlier or later (the weekday or day of month is kept). */
export function shiftPeriod(view: AgendaView, selected: string, dir: -1 | 1): string {
  if (view === 'day') return addDays(selected, dir);
  if (view === 'week') return addDays(selected, 7 * dir);
  return addMonths(selected, dir);
}

export function inRange(key: string, range: AgendaRange): boolean {
  return key >= range.from && key <= range.to;
}

/**
 * A Sunday-first month grid: blanks (`null`) before the 1st, the month's
 * days, and blanks up to a whole number of weeks.
 */
export function monthCells(anyDayInMonth: string): (string | null)[] {
  const first = startOfMonth(anyDayInMonth);
  const days = daysBetween(first, endOfMonth(anyDayInMonth));
  const cells: (string | null)[] = [...Array<null>(fromKey(first).getDay()).fill(null), ...days];
  while (cells.length % 7) cells.push(null);
  return cells;
}

/**
 * `/meetings` filters `meeting_datetime` (timestamptz) with `>= from` and
 * `<= to`, so plain dates would cut the last day off and use the server's
 * time zone. Send the local day bounds as instants instead.
 */
export function instantBounds(range: AgendaRange): { from: string; to: string } {
  const a = fromKey(range.from);
  const b = fromKey(range.to);
  return {
    from: new Date(a.getFullYear(), a.getMonth(), a.getDate(), 0, 0, 0, 0).toISOString(),
    to: new Date(b.getFullYear(), b.getMonth(), b.getDate(), 23, 59, 59, 999).toISOString(),
  };
}

export type GridKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Home' | 'End' | 'PageUp' | 'PageDown';

/**
 * Keyboard movement in a day grid (APG date grid). Left/right follow the
 * reading direction (in RTL, ArrowLeft is the next day), up/down move a
 * week, Home/End go to the start/end of the week, PageUp/PageDown a month.
 * Returns null for any other key.
 */
export function moveKey(key: string, pressed: string, rtl: boolean): string | null {
  switch (pressed) {
    case 'ArrowLeft': return addDays(key, rtl ? 1 : -1);
    case 'ArrowRight': return addDays(key, rtl ? -1 : 1);
    case 'ArrowUp': return addDays(key, -7);
    case 'ArrowDown': return addDays(key, 7);
    case 'Home': return startOfWeek(key);
    case 'End': return addDays(startOfWeek(key), 6);
    case 'PageUp': return addMonths(key, -1);
    case 'PageDown': return addMonths(key, 1);
    default: return null;
  }
}

/** Clamp a key into a range (a grid that does not page keeps focus at its edge). */
export function clampKey(key: string, range: AgendaRange): string {
  if (key < range.from) return range.from;
  if (key > range.to) return range.to;
  return key;
}

export interface TimedItem {
  /** ISO date-time for timed items (meetings). */
  at?: string;
}

/** Timed items first, in time order; untimed items (due dates, journal) after, in their order. */
export function sortDayItems<T extends TimedItem>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    if (a.at && b.at) return a.at.localeCompare(b.at);
    if (a.at) return -1;
    if (b.at) return 1;
    return 0;
  });
}

/**
 * Where the "עכשיו" line goes in today's sorted list: before the first timed
 * item that has not started, or after the last timed item when all are past.
 * -1 when the list has no timed items.
 */
export function nowLineIndex(items: TimedItem[], nowIso: string): number {
  const timed = items.filter((it) => it.at).length;
  if (!timed) return -1;
  const next = items.findIndex((it) => it.at && it.at >= nowIso);
  return next === -1 ? timed : next;
}

/**
 * Home always opens the agenda on today's day view (owner, 2026-10-11); week and
 * month are a click away and are not remembered between visits.
 */
export const DEFAULT_AGENDA_VIEW: AgendaView = 'day';
